import { useState } from "react";
import axios from "axios";
import { Brain, Lock, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AiLockedCard = () => (
  <Card className="shadow-lg border-slate-200 bg-slate-50/50" data-testid="ai-locked-card">
    <CardContent className="py-5">
      <div className="flex items-start gap-3">
        <div className="bg-slate-200 p-2 rounded-lg mt-0.5">
          <Lock className="h-4 w-4 text-slate-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-700 mb-1">
            Contrôle de cohérence IA & vérification annuaire (SIRET/SIREN) — réservés aux supporters
          </p>
          <p className="text-xs text-slate-500 leading-relaxed mb-3">
            Ces contrôles avancés (analyse GPT-4o des calculs, TVA, dates et interrogation de l'annuaire des
            entreprises) ont un coût d'exécution à chaque validation. Votre soutien de 2 € permet de les financer
            et de garder le validateur de base gratuit pour tous. Il débloque aussi l'<strong>explication IA de vos
            erreurs en cas de non-conformité</strong>, l'aperçu PDF lisible de vos factures et 1000 validations par mois.
          </p>
          <a
            href="https://paypal.me/LevasseurQuentin/2EUR"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ai-locked-support-link"
            className="inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Heart className="h-3.5 w-3.5" />
            Devenir supporter — 2 €
          </a>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AiExplainCard = ({ validationId }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState(null);
  const isSupporter = user && (user.is_premium || user.role === "admin");

  if (!validationId) return null;

  const handleExplain = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/validate/explain`,
        { validation_id: validationId },
        { withCredentials: true }
      );
      setExplanation(data.explanation);
    } catch (err) {
      setError(err.response?.data?.detail || "L'analyse IA a échoué. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (!isSupporter) {
    return (
      <Card className="shadow-xl border-2 border-indigo-200 bg-gradient-to-r from-indigo-50/70 to-violet-50/70" data-testid="ai-explain-locked-card">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <div className="bg-indigo-100 p-2 rounded-lg mt-0.5">
              <Brain className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2 flex-wrap">
                Document non conforme ? L'IA vous explique quoi corriger
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Réservé aux supporters</Badge>
              </p>
              <p className="text-xs text-slate-500 leading-relaxed mb-3">
                Les supporters bénéficient d'une analyse IA détaillée : chaque erreur est expliquée en français
                simple avec la correction précise à apporter dans le XML. Cette analyse mobilise une IA dont
                l'exécution a un coût — votre soutien de 2 € la finance.
              </p>
              <a
                href="https://paypal.me/LevasseurQuentin/2EUR"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ai-explain-support-link"
                className="inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200 px-3 py-1.5 rounded-lg transition-colors"
              >
                <Heart className="h-3.5 w-3.5" />
                Devenir supporter — 2 €
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50/60 to-violet-50/60" data-testid="ai-explain-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-indigo-700 flex-wrap">
          <Brain className="h-5 w-5" />
          Analyse IA des erreurs
          <Badge variant="outline" className="border-indigo-300 text-indigo-600 text-xs font-normal">
            GPT-4o
          </Badge>
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
            Avantage supporter
          </Badge>
        </CardTitle>
        <CardDescription className="text-slate-500">
          En tant que supporter, demandez une explication détaillée de chaque erreur de non-conformité et les corrections concrètes à apporter
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!explanation && (
          <Button
            onClick={handleExplain}
            disabled={loading}
            data-testid="ai-explain-button"
            className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold rounded-xl gap-2"
          >
            {loading ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Expliquer les erreurs avec l'IA
              </>
            )}
          </Button>
        )}
        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="ai-explain-error">{error}</p>
        )}
        {explanation && (
          <div
            className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-5 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
            data-testid="ai-explain-result"
          >
            {explanation}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
